import React, { useState, useEffect } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, getDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { getAppointments } from '../../services/appointmentService';
import MonthSelector from '../../components/calendar/MonthSelector';
import CalendarGrid from '../../components/calendar/CalendarGrid';
import AppointmentList from '../../components/calendar/AppointmentList';
import './Calendar.css';

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchAppointments();
  }, [currentDate]);
  
  const fetchAppointments = async () => {
    try {
      setLoading(true);
      
      const startDate = startOfMonth(currentDate);
      const endDate = endOfMonth(currentDate);
      
      const data = await getAppointments(startDate, endDate);
      setAppointments(data);
      setError(null);
    } catch (err) {
      setError('Error al cargar las citas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePrevMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };
  
  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };
  
  // Manejar la actualización de una cita (asistencia)
  const handleAppointmentUpdate = (updatedAppointment) => {
    setAppointments(appointments.map(app => 
      app.id === updatedAppointment.id ? updatedAppointment : app
    ));
  };
  
  const days = eachDayOfInterval({
    start: startOfMonth(currentDate),
    end: endOfMonth(currentDate)
  });
  
  // Filtrar citas para el día actual
  const todayAppointments = appointments.filter(app => 
    new Date(app.date).toDateString() === new Date().toDateString()
  );
  
  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <h1 className="page-title">Agenda</h1>
        
        <a 
          href="https://calendar.google.com"
          target="_blank"
          rel="noopener noreferrer"
          className="calendar-action"
        >
          <FiCalendar className="action-icon" />
          <span>Mostrar en Calendar</span>
        </a>
      </div>
      
      <div className="month-navigation">
        <button className="month-nav-button" onClick={handlePrevMonth}>
          <FiChevronLeft />
        </button>
        
        <h2 className="current-month">
          {format(currentDate, 'MMMM yyyy', { locale: es })}
        </h2>
        
        <button className="month-nav-button" onClick={handleNextMonth}>
          <FiChevronRight />
        </button>
      </div>
      
      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando citas...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchAppointments} className="retry-button">Reintentar</button>
        </div>
      ) : (
        <>
          <CalendarGrid 
            days={days} 
            appointments={appointments} 
            currentDate={currentDate}
            onAppointmentUpdate={handleAppointmentUpdate}
          />
          
          {todayAppointments.length > 0 && (
            <div className="today-appointments">
              <h3 className="today-title">Citas para hoy</h3>
              <AppointmentList appointments={todayAppointments} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Calendar;